/*
  ZH · Cesta lateral (sections/zh-atc-popup.liquid)

  Se abre:
  - con el aviso «zh:cart-added» que lanzan las secciones propias al añadir:
      document.dispatchEvent(new CustomEvent('zh:cart-added', { detail: { item, variantId, quantity } }));
  - desde product-form.js de Dawn (ficha y quick add), que la usa como su «cart»
    con getSectionsToRender(), setActiveElement() y renderContents();
  - al pulsar el icono de la cesta del header (#sn-cart-btn).

  El contenido de la cesta lo pinta Liquid: tras cada cambio se piden las partes
  [data-zh-cd-part] con Section Rendering. Tras cada cambio se avisa al header con
  «cart:updated» (con la cesta) o «cart:refresh» (sin ella).

  «Te puede interesar» (bloque bajo el subtotal) y «Vistos recientemente» (pestaña)
  se pintan aquí, con la misma tarjeta.

  window.ZHAtc.enabled indica a las secciones que la cesta lateral está activa
  (para no mostrar a la vez sus propios avisos).
*/
if (!customElements.get('zh-atc-popup')) {
  const RECENT_KEY = 'zh-recent-products';

  customElements.define(
    'zh-atc-popup',
    class ZhCartDrawer extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.panel = this.querySelector('.zh-cd__panel');
        this.body = this.querySelector('[data-zh-cd-body]');
        this.live = this.querySelector('[data-zh-cd-live]');
        this.recoBox = this.querySelector('[data-zh-cd-reco]');
        this.recoList = this.querySelector('[data-zh-cd-reco-list]');
        this.recoMore = this.querySelector('[data-zh-cd-reco-more]');
        this.recentList = this.querySelector('[data-zh-cd-recent-list]');
        this.recentEmpty = this.querySelector('[data-zh-cd-recent-empty]');
        this.summaryEnd = this.querySelector('[data-zh-cd-summary-end]');
        this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
        this.isOpen = false;
        this.queue = Promise.resolve();
        this.recoKey = null;
        this.recoToken = 0;
        this.recoPool = [];
        this.recoShown = 0;

        const fallbackEl = this.querySelector('[data-zh-cd-fallback]');
        this.fallback = fallbackEl ? this.parseJSON(fallbackEl.textContent, []) : [];

        this.onClick = this.onClick.bind(this);
        this.onTabKeys = this.onTabKeys.bind(this);
        this.onKeyDown = (evt) => {
          if (evt.key === 'Escape') {
            evt.preventDefault();
            this.close();
          } else if (evt.key === 'Tab') {
            this.trapTab(evt);
          }
        };
        this.onAdded = () => {
          this.open();
          this.refresh();
        };
        // Botón que abrió la cesta: algunas secciones lo desactivan mientras añaden y pierde el foco
        this.onTrigger = (evt) => {
          const trigger = evt.target.closest && evt.target.closest('button, a, [tabindex]');
          if (trigger && !this.contains(trigger)) this.lastTrigger = trigger;
        };
        this.onCartIcon = (evt) => {
          if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
          evt.preventDefault();
          this.open(evt.currentTarget);
          this.refresh();
        };

        this.addEventListener('click', this.onClick);
        this.querySelector('[role="tablist"]').addEventListener('keydown', this.onTabKeys);
        document.addEventListener('zh:cart-added', this.onAdded);
        document.addEventListener('click', this.onTrigger, true);

        // El icono de la cesta del header abre el panel (en la página de la cesta, no)
        this.cartIcon = document.getElementById('sn-cart-btn');
        if (this.cartIcon && this.dataset.cartPage !== 'true') {
          this.cartIcon.setAttribute('aria-haspopup', 'dialog');
          this.cartIcon.addEventListener('click', this.onCartIcon);
        }

        this.setupObservers();
        this.saveRecent();
        this.setupEditor();

        window.ZHAtc = {
          enabled: true,
          open: () => {
            this.open();
            this.refresh();
          },
          close: () => this.close(),
          refresh: () => this.refresh(),
        };
        window.ZHAtc.show = window.ZHAtc.open;
      }

      disconnectedCallback() {
        document.removeEventListener('zh:cart-added', this.onAdded);
        document.removeEventListener('click', this.onTrigger, true);
        document.removeEventListener('keydown', this.onKeyDown);
        document.body.classList.remove('overflow-hidden');
        if (this.cartIcon) {
          this.cartIcon.removeEventListener('click', this.onCartIcon);
          this.cartIcon.removeAttribute('aria-haspopup');
        }
        if (this.stickyObserver) this.stickyObserver.disconnect();
        if (this.moreObserver) this.moreObserver.disconnect();
        if (this.onEditorSelect) {
          document.removeEventListener('shopify:section:select', this.onEditorSelect);
          document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
        }
        if (window.ZHAtc && window.ZHAtc.close) delete window.ZHAtc;
      }

      /* ── Interfaz de «cart» para product-form.js de Dawn ── */

      getSectionsToRender() {
        return [{ id: this.sectionId }];
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      renderContents(parsedState) {
        if (parsedState && parsedState.sections && parsedState.sections[this.sectionId]) {
          this.applySections(parsedState.sections);
        } else {
          this.refresh();
        }
        this.open();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      /* ── Abrir y cerrar ── */

      open(trigger) {
        this.selectTab('cart');
        if (this.isOpen) return;
        this.isOpen = true;

        const active = document.activeElement;
        this.returnFocus =
          trigger || this.activeElement || (active && active !== document.body ? active : this.lastTrigger) || null;
        this.activeElement = null;

        this.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeyDown);
        this.panel.focus({ preventScroll: true });
        this.loadReco();
      }

      close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeyDown);

        const target = this.returnFocus && document.contains(this.returnFocus) ? this.returnFocus : null;
        if (target) target.focus({ preventScroll: true });
        this.returnFocus = null;
      }

      trapTab(evt) {
        const focusables = Array.from(
          this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex="0"]')
        ).filter((el) => el.getClientRects().length && !el.closest('[hidden]') && el.tabIndex !== -1);
        if (!focusables.length) {
          evt.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (evt.shiftKey && (active === first || active === this.panel || !this.panel.contains(active))) {
          evt.preventDefault();
          last.focus();
        } else if (!evt.shiftKey && (active === last || !this.panel.contains(active))) {
          evt.preventDefault();
          first.focus();
        }
      }

      /* ── Clics dentro del panel ── */

      onClick(evt) {
        const target = evt.target;

        if (target.closest('[data-zh-cd-close]')) {
          evt.preventDefault();
          this.close();
          return;
        }

        const tab = target.closest('[data-zh-cd-tab]');
        if (tab) {
          this.selectTab(tab.dataset.zhCdTab);
          return;
        }

        const line = target.closest('[data-key]');
        const qtyBtn = target.closest('[data-zh-cd-qty]');
        if (qtyBtn && line) {
          const step = parseInt(qtyBtn.dataset.zhCdQty, 10);
          const quantity = Math.max(0, parseInt(line.dataset.quantity, 10) + step);
          this.changeLine(line.dataset.key, quantity, `[data-key="${line.dataset.key}"] [data-zh-cd-qty="${step}"]`);
          return;
        }
        if (target.closest('[data-zh-cd-remove]') && line) {
          this.changeLine(line.dataset.key, 0, null);
          return;
        }

        const addBtn = target.closest('[data-zh-cd-add]');
        if (addBtn) this.addProduct(addBtn);
      }

      /* ── Pestañas ── */

      selectTab(name, focus = false) {
        const tabs = this.querySelectorAll('[data-zh-cd-tab]');
        if (!Array.from(tabs).some((t) => t.dataset.zhCdTab === name)) name = 'cart';
        tabs.forEach((tab) => {
          const on = tab.dataset.zhCdTab === name;
          tab.setAttribute('aria-selected', on);
          tab.tabIndex = on ? 0 : -1;
          if (on && focus) tab.focus();
        });
        this.querySelectorAll('[data-zh-cd-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.zhCdPanel !== name;
        });
        if (this.body) this.body.scrollTop = 0;
        if (name === 'recent') this.renderRecent();
      }

      onTabKeys(evt) {
        const tabs = Array.from(this.querySelectorAll('[data-zh-cd-tab]'));
        const index = tabs.indexOf(document.activeElement);
        if (index === -1) return;
        let next = null;
        if (evt.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        else if (evt.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (evt.key === 'Home') next = tabs[0];
        else if (evt.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        evt.preventDefault();
        this.selectTab(next.dataset.zhCdTab, true);
      }

      /* ── Observadores: subtotal fijo y carga progresiva ── */

      setupObservers() {
        if (!('IntersectionObserver' in window) || !this.body) return;

        // El subtotal está fijo mientras su final natural queda por debajo del área visible
        if (this.summaryEnd) {
          this.stickyObserver = new IntersectionObserver(
            ([entry]) => {
              const below = !entry.isIntersecting && entry.rootBounds && entry.boundingClientRect.top > entry.rootBounds.bottom;
              this.classList.toggle('is-summary-stuck', Boolean(below) && Boolean(this.querySelector('.zh-cd__summary')));
            },
            { root: this.body, threshold: 0 }
          );
          this.stickyObserver.observe(this.summaryEnd);
        }

        // Al acercarse al final de «Te puede interesar», se cargan más productos
        if (this.recoMore) {
          this.moreObserver = new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) this.showMoreReco(true);
            },
            { root: this.body, rootMargin: '0px 0px 160px 0px', threshold: 0 }
          );
          this.moreObserver.observe(this.recoMore);
        }
      }

      /* ── Cesta: Section Rendering ── */

      root() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      }

      refresh() {
        this.classList.add('is-busy');
        return fetch(`${this.root()}?sections=${encodeURIComponent(this.sectionId)}`)
          .then((res) => res.json())
          .then((sections) => {
            this.applySections(sections);
            // El header vuelve a leer la cesta: así el contador siempre coincide con el panel
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          })
          .catch(() => {})
          .finally(() => this.classList.remove('is-busy'));
      }

      applySections(sections, focusSelector) {
        const html = sections && sections[this.sectionId];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.querySelectorAll('[data-zh-cd-part]').forEach((part) => {
          const fresh = doc.querySelector(`[data-zh-cd-part="${part.dataset.zhCdPart}"]`);
          if (fresh) part.innerHTML = fresh.innerHTML;
        });

        // Sin bloque de subtotal (cesta vacía) no hay nada fijo
        if (!this.querySelector('.zh-cd__summary')) this.classList.remove('is-summary-stuck');
        if (this.isOpen) this.loadReco();
        const recentPanel = this.querySelector('[data-zh-cd-panel="recent"]');
        if (recentPanel && !recentPanel.hidden) this.renderRecent();

        if (focusSelector) {
          const el = this.querySelector(focusSelector);
          const tab = this.querySelector('[data-zh-cd-tab="cart"]');
          if (el) el.focus({ preventScroll: true });
          else if (tab && this.isOpen) tab.focus({ preventScroll: true });
        }
      }

      cartProductIds() {
        const holder = this.querySelector('[data-zh-cd-ids]');
        return new Set(holder ? holder.dataset.zhCdIds.split(',').filter(Boolean) : []);
      }

      cartHandles() {
        const holder = this.querySelector('[data-zh-cd-handles]');
        return new Set(holder ? holder.dataset.zhCdHandles.split(',').filter(Boolean) : []);
      }

      inCart(product) {
        return this.cartProductIds().has(String(product.id)) || this.cartHandles().has(product.handle);
      }

      changeLine(key, quantity, focusSelector) {
        this.queue = this.queue.then(async () => {
          this.classList.add('is-busy');
          const changeUrl = `${(window.routes && window.routes.cart_change_url) || '/cart/change'}.js`;
          try {
            const res = await fetch(changeUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                id: key,
                quantity,
                sections: [this.sectionId],
                sections_url: window.location.pathname,
              }),
            });
            const data = await res.json();
            if (data.status || !data.sections) {
              this.showLineError(key, data.description || data.message);
              return;
            }
            // Al eliminar, el foco pasa a la pestaña Cesta
            this.applySections(data.sections, focusSelector || '[data-zh-cd-tab="cart"]');
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: data } }));
            this.announce(this.dataset.updatedText);
          } catch (e) {
            this.showLineError(key);
          } finally {
            this.classList.remove('is-busy');
          }
        });
        return this.queue;
      }

      showLineError(key, message) {
        const text = message || this.dataset.errorText;
        const line = this.querySelector(`[data-key="${key}"]`);
        if (line) {
          let error = line.querySelector('.zh-cd__error');
          if (!error) {
            error = document.createElement('p');
            error.className = 'zh-cd__error';
            line.appendChild(error);
          }
          error.textContent = text;
        }
        this.announce(text);
      }

      announce(text) {
        if (!this.live || !text) return;
        this.live.textContent = '';
        setTimeout(() => {
          this.live.textContent = text;
        }, 50);
      }

      /* ── Te puede interesar: recomendaciones según la cesta + colección de relleno ── */

      async loadReco() {
        if (!this.recoBox) return;
        const inCart = this.cartProductIds();
        // Solo se recalcula si cambian los productos de la cesta (no al cambiar cantidades)
        const key = Array.from(inCart).sort().join(',');
        if (key === this.recoKey) return;
        this.recoKey = key;
        const token = ++this.recoToken;
        const max = parseInt(this.dataset.recoMax, 10) || 12;
        const base =
          (window.routes && window.routes.product_recommendations_url) || `${this.root()}recommendations/products`;

        const seeds = Array.from(inCart).slice(0, 3);
        const results = await Promise.all(
          seeds.map((id) =>
            fetch(`${base}.json?product_id=${id}&limit=10&intent=related`)
              .then((res) => (res.ok ? res.json() : { products: [] }))
              .catch(() => ({ products: [] }))
          )
        );
        if (token !== this.recoToken) return;

        const candidates = [];
        results.forEach((result) => (result.products || []).forEach((p) => candidates.push(this.fromApi(p))));
        candidates.push(...this.fallback);

        const handles = this.cartHandles();
        // En una ficha, tampoco se recomienda el producto que se está viendo
        const seen = new Set(this.dataset.currentProduct ? [String(this.dataset.currentProduct)] : []);
        this.recoPool = [];
        for (const product of candidates) {
          const id = String(product.id);
          if (seen.has(id) || inCart.has(id) || handles.has(product.handle) || !product.available) continue;
          seen.add(id);
          this.recoPool.push(product);
          if (this.recoPool.length >= max) break;
        }

        this.recoList.textContent = '';
        this.recoShown = 0;
        this.recoBox.hidden = this.recoPool.length === 0;
        this.showMoreReco(false);
      }

      showMoreReco(fade) {
        if (!this.recoList || this.recoShown >= this.recoPool.length) return;
        const count = this.recoShown === 0 ? parseInt(this.dataset.recoInitial, 10) || 4 : parseInt(this.dataset.recoStep, 10) || 4;
        const next = this.recoPool.slice(this.recoShown, this.recoShown + count);
        next.forEach((product) => this.recoList.appendChild(this.buildCard(product, fade)));
        this.recoShown += next.length;

        // Si el final del bloque sigue a la vista, el observador no vuelve a avisar: se comprueba aquí
        if (fade && this.recoShown < this.recoPool.length) {
          requestAnimationFrame(() => {
            if (!this.recoMore || !this.body) return;
            const more = this.recoMore.getBoundingClientRect();
            const view = this.body.getBoundingClientRect();
            if (more.top < view.bottom + 160) this.showMoreReco(true);
          });
        }
      }

      fromApi(p) {
        const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0] || {};
        const image = p.featured_image || (p.media && p.media[0] && p.media[0].src) || '';
        return {
          id: p.id,
          handle: p.handle,
          title: p.title,
          url: p.url || `${this.root()}products/${p.handle}`,
          image: image && image.startsWith('//') ? `https:${image}` : image,
          alt: p.title,
          price: p.price,
          compare: p.compare_at_price || 0,
          available: p.available,
          variantId: variant.id,
          single: (p.variants || []).length <= 1,
        };
      }

      /* ── Tarjeta común de «Te puede interesar» y «Vistos recientemente» ── */

      buildCard(product, fade) {
        const li = document.createElement('li');
        li.className = fade ? 'zh-cd__card is-new' : 'zh-cd__card';

        const link = document.createElement('a');
        link.className = 'zh-cd__card-link';
        link.href = product.url;

        const media = document.createElement('span');
        media.className = 'zh-cd__card-media';
        if (product.image) {
          const img = document.createElement('img');
          img.src = this.sizedImage(product.image, 400);
          img.srcset = `${this.sizedImage(product.image, 300)} 300w, ${this.sizedImage(product.image, 600)} 600w`;
          img.sizes = '(max-width: 749px) 50vw, 220px';
          img.alt = product.alt || '';
          img.loading = 'lazy';
          img.width = 400;
          img.height = 500;
          media.appendChild(img);
        }
        const scrim = document.createElement('span');
        scrim.className = 'zh-cd__card-scrim';
        media.appendChild(scrim);

        const info = document.createElement('span');
        info.className = 'zh-cd__card-info';
        const name = document.createElement('span');
        name.className = 'zh-cd__card-name';
        name.textContent = product.title;
        info.appendChild(name);
        if (typeof product.price === 'number') {
          const prices = document.createElement('span');
          prices.className = 'zh-cd__card-prices';
          const price = document.createElement('span');
          price.textContent = this.formatMoney(product.price);
          prices.appendChild(price);
          if (product.compare > product.price) {
            const compare = document.createElement('s');
            compare.textContent = this.formatMoney(product.compare);
            prices.appendChild(compare);
          }
          info.appendChild(prices);
        }
        link.append(media, info);
        li.appendChild(link);

        let action = null;
        if (product.single && product.variantId && product.available !== false) {
          action = document.createElement('button');
          action.type = 'button';
          action.dataset.zhCdAdd = '';
          action.dataset.variantId = product.variantId;
          action.textContent = this.dataset.recoAdd;
          action.setAttribute('aria-label', `${this.dataset.recoAdd}: ${product.title}`);
        } else if (!product.single && product.available !== false && product.url) {
          // Con varias opciones se elige en la ficha; los agotados quedan sin botón
          action = document.createElement('a');
          action.href = product.url;
          action.textContent = this.dataset.recoOptions;
          action.setAttribute('aria-label', `${this.dataset.recoOptions}: ${product.title}`);
        }
        if (action) {
          action.className = 'zh-cd__btn zh-cd__btn--add';
          li.appendChild(action);
        }
        return li;
      }

      addProduct(button) {
        const label = button.textContent;
        const fromRecent = Boolean(button.closest('[data-zh-cd-panel="recent"]'));
        button.disabled = true;
        button.textContent = '…';
        this.queue = this.queue.then(async () => {
          try {
            const res = await fetch(`${this.root()}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: parseInt(button.dataset.variantId, 10), quantity: 1 }],
                sections: [this.sectionId],
                sections_url: window.location.pathname,
              }),
            });
            const data = await res.json();
            if (data.status || !data.sections) {
              button.disabled = false;
              button.textContent = label;
              this.announce(data.description || this.dataset.errorText);
              return;
            }
            this.applySections(data.sections);
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            // Se vuelve a la lista de la cesta para ver el producto añadido
            if (fromRecent) this.selectTab('cart', true);
            else {
              const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              this.body.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
              const tab = this.querySelector('[data-zh-cd-tab="cart"]');
              if (tab) tab.focus({ preventScroll: true });
            }
            this.announce(this.dataset.updatedText);
          } catch (e) {
            button.disabled = false;
            button.textContent = label;
            this.announce(this.dataset.errorText);
          }
        });
      }

      /* ── Vistos recientemente (en el navegador del cliente) ── */

      readRecent() {
        try {
          const list = this.parseJSON(window.localStorage.getItem(RECENT_KEY), []);
          return Array.isArray(list) ? list : [];
        } catch (e) {
          return [];
        }
      }

      saveRecent() {
        const currentEl = this.querySelector('[data-zh-cd-current]');
        if (!currentEl) return;
        const current = this.parseJSON(currentEl.textContent, null);
        if (!current || !current.handle) return;
        this.currentHandle = current.handle;
        const list = [current, ...this.readRecent().filter((p) => p.handle !== current.handle)].slice(0, 24);
        try {
          window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
        } catch (e) {
          /* navegación privada: no se guarda */
        }
      }

      renderRecent() {
        if (!this.recentList) return;
        const limit = parseInt(this.dataset.recentLimit, 10) || 8;
        const items = this.readRecent()
          .filter((p) => p && p.handle && p.image && p.handle !== this.currentHandle && !this.inCart(p))
          .slice(0, limit);

        this.recentList.textContent = '';
        items.forEach((p) => this.recentList.appendChild(this.buildCard(p, false)));
        this.recentList.hidden = items.length === 0;
        if (this.recentEmpty) this.recentEmpty.hidden = items.length > 0;
      }

      /* ── Utilidades ── */

      parseJSON(text, fallback) {
        try {
          return text ? JSON.parse(text) : fallback;
        } catch (e) {
          return fallback;
        }
      }

      sizedImage(src, width) {
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', width);
          return url.toString();
        } catch (e) {
          return src;
        }
      }

      formatMoney(cents) {
        const value = (Number(cents) || 0) / 100;
        const pattern = /\{\{\s*(\w+)\s*\}\}/;
        const match = this.moneyFormat.match(pattern);
        const format = (decimals, thousands, decimal) => {
          const [int, dec] = value.toFixed(decimals).split('.');
          const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return dec ? grouped + decimal + dec : grouped;
        };
        const amounts = {
          amount: format(2, ',', '.'),
          amount_no_decimals: format(0, ',', '.'),
          amount_with_comma_separator: format(2, '.', ','),
          amount_no_decimals_with_comma_separator: format(0, '.', ','),
          amount_with_space_separator: format(2, ' ', ','),
          amount_no_decimals_with_space_separator: format(0, ' ', ','),
          amount_with_apostrophe_separator: format(2, "'", '.'),
        };
        const key = match ? match[1] : 'amount_with_comma_separator';
        return this.moneyFormat.replace(pattern, amounts[key] || amounts.amount_with_comma_separator);
      }

      /* ── Editor de temas: se abre al seleccionar la sección ── */

      setupEditor() {
        if (!window.Shopify || !window.Shopify.designMode) return;
        this.onEditorSelect = (evt) => {
          if (evt.detail.sectionId !== this.sectionId) return;
          window.zhCdSelected = true;
          this.open();
        };
        this.onEditorDeselect = (evt) => {
          if (evt.detail.sectionId !== this.sectionId) return;
          window.zhCdSelected = false;
          this.close();
        };
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);

        // Al cambiar un ajuste, Shopify vuelve a pintar la sección: se reabre
        if (window.zhCdSelected) this.open();
      }
    }
  );
}
